import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useCreateRoom, useRotateInvite, useStudioRooms } from '../lib/studio/studio-api';
import { errorInfo } from '../lib/api';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { EmptyState, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { CopyButton } from '../components/shows/bits';

export function StudioPage() {
  const rooms = useStudioRooms();
  const create = useCreateRoom();
  const rotate = useRotateInvite();
  const toast = useToast();
  const [name, setName] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(name.trim(), {
      onSuccess: () => setName(''),
      onError: (err) => toast.show(errorInfo(err).message, 'error'),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Studio"
        description="Record interviews in the browser. Guests join from a link — no account — and everyone is recorded in full quality on their own computer."
      />
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <TextField label="New room" placeholder="e.g. Episode 13 with Dana" value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          Create room
        </Button>
      </form>

      {rooms.isLoading && <Loading />}
      {rooms.isError && <ErrorMessage error={rooms.error} onRetry={() => rooms.refetch()} />}
      {rooms.data?.length === 0 && <EmptyState title="No rooms yet" body="Create a room, send your guest the link, and press record." />}
      {!!rooms.data?.length && (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {rooms.data.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted">Created {new Date(r.createdAt).toLocaleDateString()}</div>
              </div>
              {r.inviteUrl && <CopyButton value={r.inviteUrl} label="Copy guest link" />}
              <Button
                size="sm"
                variant="ghost"
                loading={rotate.isPending && rotate.variables === r.id}
                onClick={() =>
                  rotate.mutate(r.id, {
                    onSuccess: () => toast.show('New guest link — the old one no longer works.', 'success'),
                    onError: (err) => toast.show(errorInfo(err).message, 'error'),
                  })
                }
              >
                New link
              </Button>
              <Link to={`/studio/${r.id}`} className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast hover:bg-accent-hover">
                Open studio
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
